/**
 * Simulated network round-trip for the mock layer.
 *
 * Why this matters: services backed by localStorage resolve synchronously,
 * so any `await service.x()` returns within a microtask. UI states like
 * `isSaving=true` flip to `false` in the same paint, which means loaders
 * (spinners, FullPageDotsLoader, button loaders) never become visible.
 *
 * Sprinkling `await simulateLatency()` at the start of persistence-causing
 * service methods makes the mock feel production-like — and gives users the
 * visual feedback we already built. When the real backend lands, every
 * call site stays unchanged; we'll either delete the helper or let it
 * compile to a no-op in production.
 *
 * Disable globally in tests via:
 *   `setMockLatencyEnabled(false)` (e.g. in setupTests.ts)
 */

const DEFAULT_LATENCY_MS = 600
const DEFAULT_JITTER_MS = 200

let enabled = true

/** Pause for `ms` (+ random jitter). No-op when disabled. */
export const simulateLatency = (
  ms: number = DEFAULT_LATENCY_MS,
  jitterMs: number = DEFAULT_JITTER_MS,
): Promise<void> => {
  if (!enabled) return Promise.resolve()
  const total = ms + Math.random() * jitterMs
  return new Promise((resolve) => setTimeout(resolve, total))
}

export const setMockLatencyEnabled = (next: boolean): void => {
  enabled = next
}

export const isMockLatencyEnabled = (): boolean => enabled
